import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Quantity selector component.
 *
 * PICO&PALA STOCK CONTROL
 *
 * When the selected quantity exceeds available Shopify inventory:
 * - the selected quantity remains visible;
 * - a stock warning is shown;
 * - Add to cart is disabled;
 * - accelerated checkout is hidden by buy-buttons CSS;
 * - when quantity becomes valid again, purchasing is restored.
 *
 * Existing units of the same variant already in the cart are also counted.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];

  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  stockInputHandler = null;

  connectedCallback() {
    super.connectedCallback();

    const { quantityInput, minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }

    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.stockInputHandler = () => {
      this.updateButtonStates();
      this.updateStockState();
    };

    quantityInput.addEventListener('input', this.stockInputHandler);

    this.initialized = true;

    this.updateButtonStates();
    this.updateStockState();
  }

  disconnectedCallback() {
    const { quantityInput } = this.refs;

    if (this.stockInputHandler && quantityInput) {
      quantityInput.removeEventListener('input', this.stockInputHandler);
    }

    super.disconnectedCallback();
  }

  /**
   * Update quantity currently in cart.
   *
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute(
      'data-cart-quantity',
      cartQty.toString()
    );

    this.updateCartQuantity();
  }

  /**
   * Check whether current quantity can be added.
   */
  canAddToCart() {
    const {
      max,
      cartQuantity,
      value,
    } = this.getCurrentValues();

    const wouldExceedMaximum =
      max !== null &&
      cartQuantity + value > max;

    const wouldExceedStock =
      this.isStockExceeded();

    return {
      canAdd:
        !wouldExceedMaximum &&
        !wouldExceedStock,

      maxQuantity: max,
      cartQuantity,
      quantityToAdd: value,
    };
  }

  /**
   * Get current input value.
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Set input value.
   *
   * @param {string} value
   */
  setValue(value) {
    this.refs.quantityInput.value = value;

    this.updateButtonStates();
    this.updateStockState();
  }

  /**
   * Update Shopify quantity rule constraints.
   *
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    const currentValue =
      parseInt(quantityInput.value) || 0;

    quantityInput.min = min;

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.step = step;

    const newMin =
      parseIntOrDefault(min, 1);

    const newStep =
      parseIntOrDefault(step, 1);

    const effectiveMax =
      this.getEffectiveMax();

    let newValue = currentValue;

    if (
      (currentValue - newMin) % newStep !== 0
    ) {
      newValue =
        newMin +
        Math.floor(
          (currentValue - newMin) / newStep
        ) *
          newStep;
    }

    newValue = Math.max(
      newMin,
      Math.min(
        effectiveMax !== null
          ? effectiveMax
          : Infinity,
        newValue
      )
    );

    if (newValue !== currentValue) {
      quantityInput.value =
        newValue.toString();
    }

    this.updateButtonStates();
    this.updateStockState();
  }

  /**
   * Read quantity values from DOM.
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(
        quantityInput.min,
        1
      ),

      max: parseIntOrDefault(
        quantityInput.max,
        null
      ),

      step: parseIntOrDefault(
        quantityInput.step,
        1
      ),

      value: parseIntOrDefault(
        quantityInput.value,
        0
      ),

      cartQuantity: parseIntOrDefault(
        quantityInput.getAttribute(
          'data-cart-quantity'
        ),
        0
      ),
    };
  }

  /**
   * Maximum imposed by Shopify quantity rules.
   *
   * This is NOT inventory stock.
   */
  getEffectiveMax() {
    const {
      max,
      cartQuantity,
      min,
    } = this.getCurrentValues();

    if (max === null) {
      return null;
    }

    return Math.max(
      max - cartQuantity,
      min
    );
  }

  /**
   * Read inventory data from quantity-selector.liquid.
   */
  getInventoryState() {
    const inventoryManagement =
      this.dataset.inventoryManagement;

    const inventoryPolicy =
      this.dataset.inventoryPolicy;

    const rawInventory =
      this.dataset.inventoryQuantity;

    const managed =
      inventoryManagement === 'shopify';

    const denyOverselling =
      inventoryPolicy === 'deny';

    let inventoryQuantity = null;

    if (
      rawInventory !== undefined &&
      rawInventory !== ''
    ) {
      inventoryQuantity =
        parseIntOrDefault(
          rawInventory,
          null
        );
    }

    return {
      managed,
      denyOverselling,
      inventoryQuantity,
    };
  }

  /**
   * Available units that may still be added.
   *
   * Stock minus units already in cart.
   */
  getAvailableToAdd() {
  const inventoryState =
    this.getInventoryState();

  if (
    !inventoryState.managed ||
    !inventoryState.denyOverselling ||
    inventoryState.inventoryQuantity === null
  ) {
    return null;
  }

  const {
    cartQuantity,
  } = this.getCurrentValues();

  return Math.max(
    0,
    inventoryState.inventoryQuantity - cartQuantity
  );
}

  /**
   * Check whether selected quantity exceeds stock.
   */
  isStockExceeded() {
    const availableToAdd =
      this.getAvailableToAdd();

    if (availableToAdd === null) {
      return false;
    }

    const {
      value,
    } = this.getCurrentValues();

    return value > availableToAdd;
  }

  /**
   * Update stock warning and purchase buttons.
   */
  updateStockState() {
    if (
      this.tagName.toLowerCase() !==
      'quantity-selector-component'
    ) {
      return;
    }

    const productForm =
      this.closest(
        'product-form-component'
      );

    if (!productForm) {
      return;
    }

    const availableToAdd =
      this.getAvailableToAdd();

    const stockExceeded =
      availableToAdd !== null &&
      this.isStockExceeded();

    productForm.classList.toggle(
      'pyp-stock-exceeded',
      stockExceeded
    );

    if (stockExceeded) {
      productForm.setAttribute(
        'data-stock-exceeded',
        'true'
      );
    } else {
      productForm.removeAttribute(
        'data-stock-exceeded'
      );
    }

    /**
     * Stock warning
     */
    const stockError =
      productForm.querySelector(
        '[data-pyp-stock-error]'
      );

    if (stockError) {
      stockError.hidden =
        !stockExceeded;

      const availableNode =
        stockError.querySelector(
          '[data-pyp-stock-available]'
        );

      if (
        availableNode &&
        availableToAdd !== null
      ) {
        availableNode.textContent =
          availableToAdd.toString();
      }
    }

    /**
     * Add to cart button
     */
    const addToCartButton =
      productForm.querySelector(
        'button[name="add"]'
      );

    if (addToCartButton) {
      if (stockExceeded) {
        if (
          !addToCartButton.hasAttribute(
            'data-pyp-original-disabled'
          )
        ) {
          addToCartButton.setAttribute(
            'data-pyp-original-disabled',
            addToCartButton.disabled
              ? 'true'
              : 'false'
          );
        }

        addToCartButton.disabled = true;

        addToCartButton.setAttribute(
          'aria-disabled',
          'true'
        );
      } else {
        if (
          addToCartButton.hasAttribute(
            'data-pyp-original-disabled'
          )
        ) {
          const originalState =
            addToCartButton.getAttribute(
              'data-pyp-original-disabled'
            );

          const originallyDisabled =
            originalState === 'true';

          addToCartButton.disabled =
            originallyDisabled;

          if (!originallyDisabled) {
            addToCartButton.removeAttribute(
              'aria-disabled'
            );
          }

          addToCartButton.removeAttribute(
            'data-pyp-original-disabled'
          );
        }
      }
    }
  }

  /**
   * Enable or disable plus/minus according
   * to Shopify quantity rules.
   *
   * Inventory stock intentionally does NOT
   * disable the plus button.
   */
  updateButtonStates() {
    const {
      minusButton,
      plusButton,
    } = this.refs;

    const {
      min,
      value,
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled =
        value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled =
        effectiveMax !== null &&
        value >= effectiveMax;
    }
  }

  /**
   * Increase or decrease quantity.
   *
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;

    const {
      min,
      step,
      value,
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    let newValue =
      value +
      step * stepMultiplier;

    newValue = Math.max(
      min,
      newValue
    );

    if (effectiveMax !== null) {
      newValue = Math.min(
        effectiveMax,
        newValue
      );
    }

    quantityInput.value =
      newValue.toString();

    this.onQuantityChange();

    this.updateButtonStates();
    this.updateStockState();
  }

  /**
   * Increase button.
   *
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(1);
  }

  /**
   * Decrease button.
   *
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(-1);
  }

  /**
   * Select input value on focus.
   *
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;

    if (
      !(
        event.target instanceof
        HTMLInputElement
      ) ||
      document.activeElement !==
        quantityInput
    ) {
      return;
    }

    quantityInput.select();
  }

  /**
   * Validate manually typed quantity.
   *
   * @param {Event} event
   */
  setQuantity(event) {
    if (
      !(
        event.target instanceof
        HTMLInputElement
      )
    ) {
      return;
    }

    event.preventDefault();

    const { quantityInput } = this.refs;

    const {
      min,
      step,
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    let quantity =
      parseInt(event.target.value) || 0;

    quantity = Math.max(
      min,
      quantity
    );

    if (effectiveMax !== null) {
      quantity = Math.min(
        effectiveMax,
        quantity
      );
    }

    if (
      (quantity - min) % step !== 0
    ) {
      quantityInput.value =
        quantity.toString();

      quantityInput.reportValidity();

      this.updateStockState();

      return;
    }

    quantityInput.value =
      quantity.toString();

    this.onQuantityChange();

    this.updateButtonStates();
    this.updateStockState();
  }

  /**
   * Dispatch quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;

    const newValue =
      parseInt(
        quantityInput.value
      );

    const cartLine =
      Number(
        quantityInput.dataset.cartLine
      ) || undefined;

    this.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        cartLine
      )
    );
  }

  /**
   * Refresh after cart changes.
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;

    const {
      min,
      value,
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    let clampedValue =
      Math.max(
        min,
        value
      );

    if (effectiveMax !== null) {
      clampedValue =
        Math.min(
          effectiveMax,
          clampedValue
        );
    }

    if (clampedValue !== value) {
      quantityInput.value =
        clampedValue.toString();
    }

    this.updateButtonStates();
    this.updateStockState();
  }

  /**
   * Get quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing quantityInput inside quantity-selector-component'
      );
    }

    return this.refs.quantityInput;
  }
}

if (
  !customElements.get(
    'quantity-selector-component'
  )
) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}2